import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from "react-router-dom";
import { FormattedMessage } from 'react-intl';
import {
  Row, Col, Layout, Form, Select, Button, Tooltip, Spin, message, AutoComplete, Typography, Alert
} from 'antd';

import { API } from '../../AxiosWrapper';
import { useHandleApiError, useFormatMessage } from '../shared/hooks'
import { UserContext } from "../shared/context"

const { Item } = Form;
const { Option } = Select;
const { Title } = Typography

const CreateUser = () => {
  const f = useFormatMessage()
  const handleApiError = useHandleApiError()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const { permissions } = useContext(UserContext)

  const [groupsLoading, setGroupsLoading] = useState(false)
  const [suggestionsLoading, setSuggestionsLoading] = useState(false)
  const [posting, setPosting] = useState(false)
  const [allGroups, setAllGroups] = useState([])
  const [user, setUser] = useState()
  const [suggestions, setSuggestions] = useState([])
  const [autocompleteText, setAutocompleteText] = useState('')
  const [saveResult, setSaveResult] = useState()

  const userId = new URL(window.location).searchParams.get('id')

  const getGroups = () => {
    setGroupsLoading(true)
    API.GET('admin/group')
      .then(({ data }) => setAllGroups(data))
      .catch(handleApiError)
      .finally(() => setGroupsLoading(false))
  }

  useEffect(getGroups, [permissions]) // eslint-disable-line react-hooks/exhaustive-deps

  const getUser = email => {
    API.GET('admin/group/withUsers')
      .then(({ data }) => {
        const userGroups = data.reduce((acc, group) =>
          group.users.some(u => u.email === email)
            ? [...acc, group.name]
            : acc
        , [])
        setUser({ email, groups: userGroups })
      })
      .catch(handleApiError)
  }

  const getEmails = () => {
    setSuggestionsLoading(true)
    API.GET('admin/group/withUsers')
      .then(({ data }) => {
        const emails = new Set()
        data.forEach(group => group.users.forEach(u => u.email && emails.add(u.email)))
        const options = [...emails].map(email => ({ label: email, value: email }))
        setSuggestions(options)
      })
      .catch(handleApiError)
      .finally(() => setSuggestionsLoading(false))
  }

  useEffect(() => {
    if (userId) getUser(userId)
    else getEmails()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => form.resetFields(), [form, user]); // https://github.com/ant-design/ant-design/issues/22372

  const handleResend = () => {
    if (!user?.groups?.length) {
      message.error(f('Generic.Error'))
      return
    }

    const url = `admin/user/${user.email}/resendInvitation`;
    API.POST(url)
      .then(() => message.success(f('Generic.Success')))
      .catch(handleApiError)
  }

  const handleCopyInviteLink = () => {
    if (!user?.groups?.length) {
      message.error(f('Generic.Error'))
      return
    }

    const url = `admin/user/${user.email}/invitationLink`;
    API.POST(url)
      .then(({ data }) => navigator.clipboard.writeText(data.link))
      .then(() => message.success(f('Generic.Success')))
      .catch(handleApiError)
  }

  const handleSubmit = ({ email, groups }) => {
    const currentUserGroups = user?.groups ?? []

    setPosting(true)
    setSaveResult(undefined)

    API.POST(`admin/user/${email}/groups`, {
      currentGroups: currentUserGroups,
      newGroups: groups,
    })
      .then(({ data }) => {
        const updatedUser = { email, groups }
        setUser(updatedUser)
        setSaveResult({
          email,
          invitationEmailSent: !!data?.invitationEmailSent,
          hasGroups: (groups?.length ?? 0) > 0,
        })
      })
      .catch(error => {
        if (error?.response?.status === 409) {
          message.error(f('Admin.UserGroupsChangedRefresh'))
          return
        }
        handleApiError(error)
      })
      .finally(() => setPosting(false))
  }

  const filteredSuggestions = autocompleteText.length
    ? suggestions.filter(obj => obj.value.includes(autocompleteText))
    : []

  if (userId && !user) return <Spin size="large" />

  return (
    <Layout.Content>
      <Row>
        <Col xs={24} lg={12}>
          <Title level={2} style={{ marginBottom: 16 }}>
            <FormattedMessage id={user ? 'CreateUser.EditUser' : 'CreateUser.AddUser'} />
          </Title>
          <Button
            type="link"
            onClick={() => navigate('/admin/access-groups')}
            className="link-style"
          >
            <FormattedMessage id="Button.BackToAccessGroups" />
          </Button>

          <Form
            form={form}
            layout="vertical"
            className="form-style"
            onFinish={handleSubmit}
            onValuesChange={() => saveResult && setSaveResult(undefined)}
            initialValues={{
              email: user?.email,
              groups: user?.groups,
            }}
          >
            <Item
              name="email"
              label={f('CreateDelivery.Email')}
              rules={[
                { type: 'email', message: f('Validation.EmailInvalid') },
                { required: true, whitespace: true, message: f('Validation.EmailRequired') }
              ]}
              hasFeedback
            >
              <AutoComplete
                type="email"
                options={filteredSuggestions}
                onSearch={setAutocompleteText}
                loading={suggestionsLoading}
              />
            </Item>
            <Item
              name="groups"
              label={f('Admin.Groups')}
              hasFeedback
            >
              <Select mode="multiple" loading={groupsLoading}>
                {allGroups.map(group => (
                  <Option key={group.name} value={group.name}>
                    <Tooltip title={group.description}>
                      {group.name}
                    </Tooltip>
                  </Option>
                ))}
              </Select>
            </Item>

            <Item>
              <Button
                type="primary"
                htmlType="submit"
                shape="round"
                loading={posting}
              >
                <FormattedMessage id="CreateSender.Save" />
              </Button>
            </Item>

            {saveResult &&
              <Alert
                type="success"
                showIcon
                style={{ marginBottom: 16 }}
                message={f('CreateUser.GroupsUpdated')}
                description={saveResult.invitationEmailSent
                  ? f('CreateUser.InvitationSent')
                  : saveResult.hasGroups
                    ? f('CreateUser.NoInvitationSent')
                    : f('CreateUser.NoGroupsNoInviteActions')}
              />
            }

            {user && user.groups?.length > 0 &&
              <>
                <Button
                  type="link"
                  style={{ padding: 0, minWidth: 'unset', marginRight: 16 }}
                  onClick={handleResend}
                >
                  <FormattedMessage id="Admin.Resend" />
                </Button>
                <Button
                  type="link"
                  style={{ padding: 0, minWidth: 'unset', marginRight: 16 }}
                  onClick={handleCopyInviteLink}
                >
                  <FormattedMessage id="Admin.CopyInviteLink" />
                </Button>
              </>
            }
          </Form>

        </Col>
      </Row>
    </Layout.Content>
  );
}

export default CreateUser;
